/**
 * Compact scenario runner — captures a real OB pricing run for a prospect
 * through the prospect's resolved pricing identity (lo-prospect-price edge
 * function). The full
 * public LO-facing pricing experience is the /lo-preview app; this is the
 * recruiter-side entry point so the pipeline holds real runs.
 *
 * Prices the same full scenario surface as the public preview (ARM, IO,
 * second liens, subordinate financing, multi-unit, non-QM doc types, DSCR,
 * credit events, prepay on investment, VA veteran type). The form speaks raw
 * OB enum values; adaptive transitions delegate to the shared enum-side
 * reducer in services/loProspectScenarioRules (parity-tested against the
 * public app's scenarioRules).
 */
import React, { useState, useRef } from 'react';
import { LoDialogDescription, LoDialogShell, LoDialogTitle } from './LoRadixOverlay';
import { runLoProspectPricing, type LoProspectScenarioInput } from '@/services/loProspectService';
import {
  applyWireScenarioChange,
  wirePurposeOptionsForLoanType,
  isSelfEmployedWireDocType,
  type WireScenarioState
} from '@/services/loProspectScenarioRules';
import { LO_PROSPECT_COMP_OPTIONS, loProspectCompLabel } from '@/shared/loProspectComp';

const PURPOSE_LABELS: Record<string, string> = {
  Purchase: 'Purchase',
  RefiRateTermLimitedCO: 'Refinance (rate/term)',
  RefiCashout: 'Cash-out refinance',
  FHAStreamlineRefi: 'FHA Streamline Refi',
  VARateReduction: 'VA Rate Reduction (IRRRL)'
};
const LOAN_TYPES = ['Conventional', 'FHA', 'VA', 'USDARuralHousing', 'NonConforming'];
const PRODUCT_TYPES = [
  { value: 'StandardProducts', label: 'Standard' },
  { value: 'ExpandedGuidelines', label: 'Expanded / Non-QM' },
  { value: 'AffordableProducts', label: 'Affordable' },
  { value: 'HUDspecialty', label: 'HUD Specialty' },
  { value: 'RenovationAndRehabilitation', label: 'Renovation & Rehab' },
  { value: 'StudentLoanCORefi', label: 'Student Loan Cash-Out' },
  { value: 'USDAStreamline', label: 'USDA Streamline' }
];
const OCCUPANCIES = [
  { value: 'PrimaryResidence', label: 'Primary residence' },
  { value: 'SecondHome', label: 'Second home' },
  { value: 'InvestmentProperty', label: 'Investment' }
];
const PROPERTY_TYPES = [
  'SingleFamily', 'Condo', 'Townhouse', 'PUD', 'DetachedCondo', 'NonWarrantableCondo',
  'Condotel', 'ManufacturedSingleWide', 'ManufacturedDoubleWide', 'Modular', 'Coop', 'Timeshare'
];
const TERMS = [30, 25, 20, 15, 10, 40];
const LOCKS = [30, 45, 60];
const UNITS = [
  { value: 'OneUnit', label: '1 unit' },
  { value: 'TwoUnits', label: '2 units' },
  { value: 'ThreeUnits', label: '3 units' },
  { value: 'FourUnits', label: '4 units' }
];
const ARM_TERMS = [
  { value: 'FiveYear', label: '5/6 ARM' },
  { value: 'SevenYear', label: '7/6 ARM' },
  { value: 'TenYear', label: '10/6 ARM' },
  { value: 'FifteenYear', label: '15/6 ARM' },
  { value: 'SixYear', label: '6/6 ARM' },
  { value: 'ThreeYear', label: '3/6 ARM' },
  { value: 'TwoYear', label: '2/6 ARM' },
  { value: 'OneYear', label: '1/6 ARM' },
  { value: 'SixMonth', label: '6mo ARM' },
  { value: 'ThreeMonth', label: '3mo ARM' },
  { value: 'OneMonth', label: '1mo ARM' }
];
const AUS_OPTIONS = [
  { value: 'NotSpecified', label: 'Not specified' },
  { value: 'DU', label: 'DU' },
  { value: 'LP', label: 'LP' },
  { value: 'InvestorAUS', label: 'Investor AUS' },
  { value: 'ManualTraditional', label: 'Manual / Traditional' }
];
const BUYDOWNS = ['None', 'OneZero', 'OneOne', 'TwoOne', 'ThreeTwoOne'];
const BUYDOWN_LABELS: Record<string, string> = {
  None: 'None', OneZero: '1-0', OneOne: '1-1', TwoOne: '2-1', ThreeTwoOne: '3-2-1'
};
const PREPAYS = [
  { value: 'None', label: 'None' },
  { value: 'OneYear', label: '1 year' },
  { value: 'TwoYear', label: '2 years' },
  { value: 'ThreeYear', label: '3 years' },
  { value: 'FourYear', label: '4 years' },
  { value: 'FiveYear', label: '5 years' }
];
const CITIZENSHIPS = [
  { value: 'USCitizen', label: 'U.S. citizen' },
  { value: 'PermResidentAlien', label: 'Permanent resident' },
  { value: 'NonPermResidentAlien', label: 'Non-permanent resident' },
  { value: 'ForeignNational', label: 'Foreign national' }
];
const VETERAN_TYPES = [
  { value: 'ActiveDuty', label: 'Active duty / veteran' },
  { value: 'NationalGuardOrReserves', label: 'National Guard / Reserves' }
];
// Artemis-proven 18 doc types; bank statements are 12/24-month only.
const DOC_TYPES = [
  { value: 'FullDoc', label: 'Full Doc' },
  { value: 'PersonalBankStmt12Mos', label: 'Personal Bank Stmt · 12 mo' },
  { value: 'PersonalBankStmt24Mos', label: 'Personal Bank Stmt · 24 mo' },
  { value: 'BusinessBankStmt12Mos', label: 'Business Bank Stmt · 12 mo' },
  { value: 'BusinessBankStmt24Mos', label: 'Business Bank Stmt · 24 mo' },
  { value: 'OneYearAltDoc', label: '1 Year Alt Doc' },
  { value: 'TwoYearAltDoc', label: '2 Year Alt Doc' },
  { value: 'AssetRelated', label: 'Asset Related' },
  { value: 'InvestorDscr', label: 'DSCR' },
  { value: 'RestrictedStock', label: 'Restricted Stock' },
  { value: 'InvestorNoRatio', label: 'No Ratio' },
  { value: 'WrittenVOE', label: 'Written VOE' },
  { value: 'Stated', label: 'Stated Income' },
  { value: 'OneYear1099', label: '1 Year 1099' },
  { value: 'TwoYear1099', label: '2 Year 1099' },
  { value: 'PLOneYear', label: 'P&L · 1 Year' },
  { value: 'PLTwoYear', label: 'P&L · 2 Year' },
  { value: 'NoIncomeVerification', label: 'No Income Verification' }
];
const HOUSING_EVENTS = ['None', 'Foreclosure', 'ShortSale', 'DeedInLieu', 'Modification', 'Forbearance'];
const HOUSING_EVENT_LABELS: Record<string, string> = {
  None: 'None', Foreclosure: 'Foreclosure', ShortSale: 'Short sale', DeedInLieu: 'Deed-in-lieu',
  Modification: 'Modification', Forbearance: 'Forbearance'
};
const SEASONINGS = [
  { value: 'Last1To12Months', label: '≤ 12 months ago' },
  { value: 'Last13To24Months', label: '13–24 months ago' },
  { value: 'Last25To36Months', label: '25–36 months ago' },
  { value: 'Last37To48Months', label: '37–48 months ago' },
  { value: 'Last49To60Months', label: '49–60 months ago' },
  { value: 'MoreThanFiveYearsAgo', label: '5+ years ago' }
];
const BANKRUPTCIES = ['None', 'Chapter7', 'Chapter11', 'Chapter13'];
const BK_LABELS: Record<string, string> = { None: 'None', Chapter7: 'Chapter 7', Chapter11: 'Chapter 11', Chapter13: 'Chapter 13' };
const BK_OUTCOMES = [
  { value: 'Discharged', label: 'Discharged' },
  { value: 'Dismissed', label: 'Dismissed' },
  { value: 'OpenOrPending', label: 'Open / pending' }
];
const MORTGAGE_LATES = ['0x30', '1x30', '2x30', '1x60', '1x90'];

const num = (v: string): number => Number(v.replace(/[^0-9.]/g, '')) || 0;

const INITIAL: WireScenarioState = {
  loanPurpose: 'Purchase',
  loanType: 'Conventional',
  productTypes: ['StandardProducts'],
  loanAmount: '560000',
  appraisedValue: '700000',
  purchasePrice: '',
  fico: '760',
  dti: '38',
  termYears: '30',
  lockDays: '30',
  compPercent: '0',
  targetRate: '6.125',
  propertyState: '',
  propertyZip: '',
  propertyCounty: '',
  propertyType: 'SingleFamily',
  occupancy: 'PrimaryResidence',
  cashOutAmount: '0',
  cashOutStash: '',
  lienType: 'First',
  firstLienAmount: '',
  secondLienAmount: '',
  amortizationType: 'Fixed',
  armFixedTerm: 'SevenYear',
  interestOnly: false,
  interestOnlyTermMonths: '',
  aus: 'NotSpecified',
  buydown: 'None',
  borrowerPaidMI: 'Yes',
  reducedMI: false,
  waiveEscrows: false,
  selfEmployed: false,
  firstTimeHomeBuyer: false,
  citizenship: 'USCitizen',
  monthsReserves: '6',
  propertiesFinanced: '',
  numberOfUnits: 'OneUnit',
  numberOfStories: '',
  prepaymentPenalty: 'None',
  veteranType: '',
  incomeVerificationType: 'FullDoc',
  dscrRatio: '',
  housingEvent: 'None',
  housingEventSeasoning: 'Last1To12Months',
  bankruptcy: 'None',
  bankruptcyOutcome: 'Discharged',
  bankruptcySeasoning: 'Last1To12Months',
  mortgageLates: '0x30',
  debtConsolidation: false,
  uniqueProperty: false
};

/** Client-side mirror of the server's requirement rules (incl. the strict-only
 *  VA/EG carve-outs) so the recruiter gets a clean message instead of a 400. */
function validateForm(f: WireScenarioState): string | null {
  if (!f.propertyState || !f.propertyZip || !f.propertyCounty) {
    return 'Property state, county and ZIP are required to price.';
  }
  const fico = num(f.fico);
  if (fico < 300 || fico > 850) {
    return 'FICO must be between 300 and 850.';
  }
  if (f.loanPurpose === 'RefiCashout' && num(f.cashOutAmount) <= 0) {
    return 'Enter a cash-out amount to price a cash-out refinance.';
  }
  if (f.loanPurpose === 'RefiCashout' && num(f.cashOutAmount) > num(f.loanAmount)) {
    return 'Cash-out amount cannot exceed the base loan amount.';
  }
  if (f.lienType === 'Second' && num(f.firstLienAmount) <= 0) {
    return 'Enter the first-lien amount to price a second lien.';
  }
  if (f.amortizationType === 'ARM' && !f.armFixedTerm) {
    return 'Pick an ARM fixed period.';
  }
  if (f.interestOnly) {
    const io = num(f.interestOnlyTermMonths);
    if (!Number.isInteger(io) || io < 12 || io > 120) {
      return 'Interest-only term must be 12–120 months.';
    }
  }
  if (f.loanType === 'VA' && !f.veteranType) {
    return 'Select the veteran type to price a VA loan.';
  }
  if (f.prepaymentPenalty !== 'None' && f.occupancy !== 'InvestmentProperty') {
    return 'Prepayment penalties price only on investment properties.';
  }
  if (f.productTypes.includes('ExpandedGuidelines')) {
    const reserves = num(f.monthsReserves);
    if (!Number.isInteger(reserves) || reserves < 0 || reserves > 120) {
      return 'Reserve months (0–120) are required for Expanded / Non-QM.';
    }
    if (f.incomeVerificationType === 'InvestorDscr') {
      const dscr = num(f.dscrRatio);
      if (!(dscr > 0) || dscr > 10) {
        return 'Enter a DSCR between 0.01 and 10.';
      }
    }
  }
  return null;
}

/** Presence-keyed scenario: omitted keys reproduce the legacy payload exactly. */
function toScenario(f: WireScenarioState): LoProspectScenarioInput {
  const isExpanded = f.productTypes.includes('ExpandedGuidelines');
  const isSecond = f.lienType === 'Second';
  return {
    loanPurpose: f.loanPurpose,
    loanType: f.loanType,
    productTypes: f.productTypes.length ? Array.from(new Set(f.productTypes)) : ['StandardProducts'],
    loanAmount: num(f.loanAmount),
    appraisedValue: num(f.appraisedValue),
    fico: num(f.fico),
    dti: num(f.dti) || undefined,
    termYears: num(f.termYears),
    lockDays: num(f.lockDays),
    compPercent: num(f.compPercent),
    targetRate: num(f.targetRate) || undefined,
    propertyState: f.propertyState.trim().toUpperCase(),
    propertyZip: f.propertyZip.trim(),
    propertyCounty: f.propertyCounty.trim(),
    propertyType: f.propertyType,
    occupancy: f.occupancy,
    cashOutAmount: f.loanPurpose === 'RefiCashout' ? num(f.cashOutAmount) : undefined,
    ...(isSecond ? { lienType: 'Second', firstLienAmount: num(f.firstLienAmount) } : {}),
    ...(!isSecond && num(f.secondLienAmount) > 0 ? { secondLienAmount: num(f.secondLienAmount) } : {}),
    ...(num(f.purchasePrice) > 0 ? { purchasePrice: num(f.purchasePrice) } : {}),
    ...(f.amortizationType === 'ARM' ? { amortizationType: 'ARM', armFixedTerm: f.armFixedTerm } : {}),
    ...(f.interestOnly ? { interestOnly: true, interestOnlyTermMonths: num(f.interestOnlyTermMonths) } : {}),
    ...(f.aus !== 'NotSpecified' ? { aus: f.aus } : {}),
    ...(f.buydown !== 'None' ? { buydown: f.buydown } : {}),
    ...(f.borrowerPaidMI === 'No' ? { borrowerPaidMI: 'No' } : {}),
    ...(f.reducedMI ? { reducedMI: true } : {}),
    ...(f.waiveEscrows ? { waiveEscrows: true } : {}),
    ...(f.selfEmployed || isSelfEmployedWireDocType(f.incomeVerificationType) ? { selfEmployed: true } : {}),
    ...(f.firstTimeHomeBuyer ? { firstTimeHomeBuyer: true } : {}),
    ...(f.citizenship !== 'USCitizen' ? { citizenship: f.citizenship } : {}),
    ...(num(f.propertiesFinanced) > 1 ? { propertiesFinanced: num(f.propertiesFinanced) } : {}),
    ...(f.numberOfUnits !== 'OneUnit' ? { numberOfUnits: f.numberOfUnits } : {}),
    ...(num(f.numberOfStories) > 1 ? { numberOfStories: num(f.numberOfStories) } : {}),
    ...(f.prepaymentPenalty !== 'None' ? { prepaymentPenalty: f.prepaymentPenalty } : {}),
    ...(f.loanType === 'VA' && f.veteranType ? { veteranType: f.veteranType } : {}),
    ...(isExpanded
      ? {
          incomeVerificationType: f.incomeVerificationType,
          monthsReserves: num(f.monthsReserves),
          ...(f.incomeVerificationType === 'InvestorDscr' ? { dscrRatio: num(f.dscrRatio) } : {}),
          ...(f.housingEvent !== 'None'
            ? { housingEvent: f.housingEvent, housingEventSeasoning: f.housingEventSeasoning }
            : {}),
          ...(f.bankruptcy !== 'None'
            ? {
                bankruptcy: f.bankruptcy,
                bankruptcyOutcome: f.bankruptcyOutcome,
                bankruptcySeasoning: f.bankruptcySeasoning
              }
            : {}),
          ...(f.mortgageLates !== '0x30' ? { mortgageLates: f.mortgageLates } : {}),
          ...(f.debtConsolidation ? { debtConsolidation: true } : {}),
          ...(f.uniqueProperty ? { uniqueProperty: true } : {})
        }
      : {})
  };
}

export function ScenarioRunnerModal({
  prospectId,
  onClose,
  onComplete,
  onToast
}: {
  prospectId: string;
  onClose: () => void;
  onComplete: () => void;
  onToast: (msg: string) => void;
}) {
  const [f, setF] = useState<WireScenarioState>(INITIAL);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busyRef = useRef(false); // synchronous double-submit guard (state lags a fast double-click)

  // All updates run through the shared enum-side transition reducer (parity-
  // tested against the public preview's scenarioRules).
  const set = (k: keyof WireScenarioState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((s) => applyWireScenarioChange(s, k, e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value));

  const isExpanded = f.productTypes.includes('ExpandedGuidelines');
  const isDscr = isExpanded && f.incomeVerificationType === 'InvestorDscr';
  const isInvestment = f.occupancy === 'InvestmentProperty';
  const selfEmployedForced = isSelfEmployedWireDocType(f.incomeVerificationType);
  const purposeOptions = wirePurposeOptionsForLoanType(f.loanType);

  const toggleProductType = (value: string) => {
    const next = f.productTypes.includes(value)
      ? f.productTypes.filter((p) => p !== value)
      : [...f.productTypes, value];
    setF((s) => applyWireScenarioChange(s, 'productTypes', next));
  };

  const submit = async () => {
    if (busyRef.current) {
      return; // a price run is already in flight (fast double-click guard)
    }
    setError(null);
    const problem = validateForm(f);
    if (problem) {
      setError(problem);
      return;
    }
    busyRef.current = true;
    const clientRequestId = crypto.randomUUID();
    const scenario = toScenario(f);
    setBusy(true);
    try {
      const res = await runLoProspectPricing(prospectId, scenario, clientRequestId);
      if (res.status !== 'priced' && res.status !== 'no_products') {
        setError(res.message ?? 'Could not price this scenario.');
        setBusy(false);
        busyRef.current = false;
        return;
      }
      if (res.status === 'no_products') {
        onToast('Run captured — no eligible products at that scenario');
      } else if (res.compFunded === false) {
        onToast(`Run captured — no investor funds ${loProspectCompLabel(num(f.compPercent))} comp at this scenario`);
      } else {
        onToast('Pricing run captured');
      }
      onComplete();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Pricing failed. Check the Optimal Blue pricing identity is configured.');
      setBusy(false);
      busyRef.current = false;
    }
  };

  const Sel = ({
    label,
    field,
    options,
    disabled
  }: {
    label: string;
    field: keyof WireScenarioState;
    options: Array<{ value: string; label: string }>;
    disabled?: boolean;
  }) => (
    <div className="field">
      <label>{label}</label>
      <select value={String(f[field])} onChange={set(field)} disabled={disabled}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );

  const Txt = ({ label, field, placeholder }: { label: string; field: keyof WireScenarioState; placeholder?: string }) => (
    <div className="field">
      <label>{label}</label>
      <input value={String(f[field])} onChange={set(field)} inputMode="numeric" placeholder={placeholder} />
    </div>
  );

  const yesNo = (field: keyof WireScenarioState, label: string, forced = false) => (
    <div className="field">
      <label>{label}</label>
      <select
        value={forced || f[field] === true ? 'Yes' : 'No'}
        disabled={forced}
        onChange={(e) => setF((s) => applyWireScenarioChange(s, field, e.target.value === 'Yes'))}
      >
        <option>No</option>
        <option>Yes</option>
      </select>
    </div>
  );

  return (
    <LoDialogShell onClose={onClose} cardClassName="wide">
          <div className="modal-head">
            <div className="modal-eyebrow">Partner Pricing Preview</div>
            <LoDialogTitle>Run a pricing scenario</LoDialogTitle>
            <LoDialogDescription>Finds the lowest rate that still pays your selected comp. Counts as one of the 5 preview runs.</LoDialogDescription>
          </div>
          <div className="modal-body">
            <div className="dw-grid">
              <Sel label="Loan purpose" field="loanPurpose" options={purposeOptions.map((v) => ({ value: v, label: PURPOSE_LABELS[v] ?? v }))} />
              <Sel label="Loan type" field="loanType" options={LOAN_TYPES.map((v) => ({ value: v, label: v }))} />
              <div className="field">
                <label>Products (multi-select)</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 0' }}>
                  {PRODUCT_TYPES.map((o) => (
                    <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={f.productTypes.includes(o.value)}
                        onChange={() => toggleProductType(o.value)}
                      />
                      {o.label}
                    </label>
                  ))}
                </div>
              </div>
              <Sel label="Lien position" field="lienType" options={[{ value: 'First', label: 'First' }, { value: 'Second', label: 'Second' }]} />
              {f.lienType === 'Second' && <Txt label="1st lien amount" field="firstLienAmount" />}
              {f.lienType !== 'Second' && <Txt label="Subordinate financing" field="secondLienAmount" placeholder="optional" />}
              <Txt label="Base loan amount" field="loanAmount" />
              <Txt label="Purchase / sales price" field="purchasePrice" placeholder="optional" />
              <Txt label="Property / appraised value" field="appraisedValue" />
              <Txt label="FICO" field="fico" />
              {f.loanPurpose === 'RefiCashout' && <Txt label="Cash-out amount" field="cashOutAmount" />}
              {isDscr ? <Txt label="DSCR" field="dscrRatio" placeholder="1.25" /> : <Txt label="DTI %" field="dti" />}
              <div className="field">
                <label>How much do you want to make?</label>
                <select value={f.compPercent} onChange={set('compPercent')}>
                  {LO_PROSPECT_COMP_OPTIONS.map((option) => (
                    <option key={option.label} value={String(option.percent)}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <Txt label="Note rate % (ref.)" field="targetRate" />
              <div className="field">
                <label>Term</label>
                <select value={f.termYears} onChange={set('termYears')}>
                  {TERMS.map((t) => (
                    <option key={t} value={t}>{t} yr</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Lock</label>
                <select value={f.lockDays} onChange={set('lockDays')}>
                  {LOCKS.map((t) => (
                    <option key={t} value={t}>{t}-day</option>
                  ))}
                </select>
              </div>
              <Sel label="Amortization" field="amortizationType" options={[{ value: 'Fixed', label: 'Fixed' }, { value: 'ARM', label: 'ARM' }]} />
              {f.amortizationType === 'ARM' && <Sel label="ARM fixed term" field="armFixedTerm" options={ARM_TERMS} />}
              {yesNo('interestOnly', 'Interest only')}
              {f.interestOnly && <Txt label="IO term (12–120 mo)" field="interestOnlyTermMonths" placeholder="120" />}
              <Sel label="Occupancy" field="occupancy" options={OCCUPANCIES} />
              <Sel label="Property type" field="propertyType" options={PROPERTY_TYPES.map((v) => ({ value: v, label: v }))} />
              <Sel label="Units" field="numberOfUnits" options={UNITS} />
              <Txt label="Stories" field="numberOfStories" placeholder="1" />
              <Sel label="Prepay penalty" field="prepaymentPenalty" options={PREPAYS} disabled={!isInvestment} />
              <Sel label="Buydown" field="buydown" options={BUYDOWNS.map((v) => ({ value: v, label: BUYDOWN_LABELS[v] }))} />
              <Sel label="AUS" field="aus" options={AUS_OPTIONS} />
              <Sel label="Citizenship" field="citizenship" options={CITIZENSHIPS} />
              {f.loanType === 'VA' && <Sel label="Veteran type" field="veteranType" options={[{ value: '', label: 'Select…' }, ...VETERAN_TYPES]} />}
              <Sel
                label="Borrower-paid MI"
                field="borrowerPaidMI"
                options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No (LPMI)' }]}
              />
              {yesNo('reducedMI', 'Reduced MI')}
              {yesNo('waiveEscrows', 'Waive escrows')}
              {yesNo('selfEmployed', selfEmployedForced ? 'Self-employed (doc type)' : 'Self-employed', selfEmployedForced)}
              {yesNo('firstTimeHomeBuyer', 'First-time buyer')}
              <Txt label="Properties financed" field="propertiesFinanced" placeholder="1" />
              {isExpanded && (
                <>
                  <Sel label="Income / doc type" field="incomeVerificationType" options={DOC_TYPES} />
                  <Txt label="Reserve months" field="monthsReserves" />
                  <Sel label="Housing event" field="housingEvent" options={HOUSING_EVENTS.map((v) => ({ value: v, label: HOUSING_EVENT_LABELS[v] }))} />
                  {f.housingEvent !== 'None' && <Sel label="Event seasoning" field="housingEventSeasoning" options={SEASONINGS} />}
                  <Sel label="Bankruptcy" field="bankruptcy" options={BANKRUPTCIES.map((v) => ({ value: v, label: BK_LABELS[v] }))} />
                  {f.bankruptcy !== 'None' && <Sel label="BK outcome" field="bankruptcyOutcome" options={BK_OUTCOMES} />}
                  {f.bankruptcy !== 'None' && <Sel label="BK seasoning" field="bankruptcySeasoning" options={SEASONINGS} />}
                  <Sel label="Mortgage lates (12mo)" field="mortgageLates" options={MORTGAGE_LATES.map((v) => ({ value: v, label: v }))} />
                  {yesNo('debtConsolidation', 'Debt consolidation')}
                  {yesNo('uniqueProperty', 'Unique property')}
                </>
              )}
              <div className="field">
                <label>State</label>
                <input value={f.propertyState} onChange={set('propertyState')} maxLength={2} placeholder="AZ" />
              </div>
              <div className="field">
                <label>County</label>
                <input value={f.propertyCounty} onChange={set('propertyCounty')} placeholder="Maricopa" />
              </div>
              <div className="field">
                <label>ZIP</label>
                <input value={f.propertyZip} onChange={set('propertyZip')} inputMode="numeric" placeholder="85254" />
              </div>
            </div>
            {error && (
              <div className="err-box">
                <div>
                  <div className="err-title">Couldn’t price this scenario</div>
                  <div className="err-detail">{error}</div>
                </div>
              </div>
            )}
          </div>
          <div className="modal-foot">
            <button className="btn-sm" onClick={onClose} type="button">
              Cancel
            </button>
            <button className="btn-sm solid" onClick={() => void submit()} disabled={busy} type="button">
              {busy ? 'Pricing…' : 'Run pricing'}
            </button>
          </div>
    </LoDialogShell>
  );
}
